import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { formatCoefficient } from '../expression.utils';
import { BaseFunctionCreator } from '../function.model';
import { QUADRATIC_VERTEX_META, type QuadraticVertexParams } from './quadratic-vertex.model';

type QuadraticVertexParamsIndexed = QuadraticVertexParams & { [key: string]: number };

export class QuadraticVertexCreator extends BaseFunctionCreator<QuadraticVertexParamsIndexed> {
  readonly functionType = 'quadratic-vertex';
  readonly defaultParams: QuadraticVertexParamsIndexed = { ...QUADRATIC_VERTEX_META.defaultParams };
  readonly availableFeatures = QUADRATIC_VERTEX_META.availableFeatures;
  readonly handleConfigs = QUADRATIC_VERTEX_META.handleConfigs;
  override readonly computedValueKeys = ['vertexX', 'vertexY', 'discriminant', 'yIntercept'] as const;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as QuadraticVertexParamsIndexed;

    const colorName = (display?.curve?.color ?? 'sky') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: QUADRATIC_VERTEX_META.defaultRanges.a },
      h: { value: initialParams.h, range: QUADRATIC_VERTEX_META.defaultRanges.h },
      k: { value: initialParams.k, range: QUADRATIC_VERTEX_META.defaultRanges.k },
    });

    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const h = paramStore.getValue('h');
      const k = paramStore.getValue('k');
      return a * Math.pow(x - h, 2) + k;
    };

    const getExpression = (): string => {
      const a = paramStore.getDisplayValue('a');
      const h = paramStore.getDisplayValue('h');
      const k = paramStore.getDisplayValue('k');
      return buildVertexFormExpression(a, h, k);
    };

    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
      getExpression,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    const getDerivative = (order = 1): ((x: number) => number) => {
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const h = paramStore.getValue('h');
          return 2 * a * (x - h);
        };
      }
      if (order === 2) {
        return () => 2 * paramStore.getValue('a');
      }
      return () => 0;
    };

    return {
      id,
      type: 'quadratic-vertex',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as QuadraticVertexParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      setDisplaySteps: (steps) => {
        for (const [name, step] of Object.entries(steps)) {
          paramStore.setDisplayStep(name, step);
        }
      },
      getDerivative,
      getExpression,
      getComputedValues: () => ({
        vertexX: () => paramStore.getValue('h'),
        vertexY: () => paramStore.getValue('k'),
        discriminant: () => -4 * paramStore.getValue('a') * paramStore.getValue('k'),
        yIntercept: () => {
          const a = paramStore.getValue('a');
          const h = paramStore.getValue('h');
          const k = paramStore.getValue('k');
          return a * h * h + k;
        },
      }),
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}

function buildVertexFormExpression(a: number, h: number, k: number): string {
  const fmt = formatCoefficient;
  let expr = '';

  const isAOne = Math.abs(a - 1) < 1e-9;
  const isANegOne = Math.abs(a + 1) < 1e-9;
  const isHZero = Math.abs(h) < 1e-9;
  const isKZero = Math.abs(k) < 1e-9;

  let xPart: string;
  if (isHZero) {
    xPart = 'x^2';
  } else if (h > 0) {
    xPart = `(x - ${fmt(h)})^2`;
  } else {
    xPart = `(x + ${fmt(-h)})^2`;
  }

  if (isAOne) {
    expr = xPart;
  } else if (isANegOne) {
    expr = `-${xPart}`;
  } else {
    expr = `${fmt(a)}${xPart}`;
  }

  if (!isKZero) {
    if (k > 0) {
      expr = `${expr} + ${fmt(k)}`;
    } else {
      expr = `${expr} - ${fmt(-k)}`;
    }
  }

  return `$${expr}$`;
}
